import { api, type BooLinkRow } from "@/lib/api";
import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

// Everything that shows a link, refreshed after it changes: both Boos'
// Sharing tabs and access histories, the dashboard map, and the org page.
export function invalidateLink(qc: QueryClient, link: Pick<BooLinkRow, "source" | "consumer"> | { sourceId?: string; consumerId?: string }) {
  const ids =
    "source" in link
      ? [link.source?.id, link.consumer?.id]
      : [(link as { sourceId?: string }).sourceId, (link as { consumerId?: string }).consumerId];
  for (const id of ids) {
    if (!id) continue;
    void qc.invalidateQueries({ queryKey: ["connections", id] });
    void qc.invalidateQueries({ queryKey: ["boo-access-events", id] });
  }
  void qc.invalidateQueries({ queryKey: ["boo-map"] });
  void qc.invalidateQueries({ queryKey: ["org-access"] });
  void qc.invalidateQueries({ queryKey: ["org-access-events"] });
}

export function useInvalidateLink() {
  const qc = useQueryClient();
  return useCallback((link: Parameters<typeof invalidateLink>[1]) => invalidateLink(qc, link), [qc]);
}

/** Who is acting: a Boo's own admins from its tab, or an org owner or admin for them. */
export type GrantActor = { kind: "boo"; containerId: string } | { kind: "org" };

export function useSourceGrantable(actor: GrantActor, link: BooLinkRow) {
  const sourceId = link.source?.id;
  return useQuery({
    queryKey: ["grantable", actor.kind, sourceId, link.id],
    queryFn: () =>
      actor.kind === "org" ? api.access.grantable(sourceId!) : api.containers.connections.grantable(sourceId!, link.id),
    enabled: !!sourceId,
    staleTime: 15_000,
  });
}
